import * as React from "react";
import { Checkbox, Divider, makeStyles } from "@fluentui/react-components";
import { FilterChipWrapper } from "./FilterChipWrapper";

const ALL_SENTINEL = "__ALL__";

const useStyles = makeStyles({
  list: {
    display: "flex",
    flexDirection: "column",
    maxHeight: "300px",
    overflowY: "auto",
  },
});

/** Option shape: key (React key), value (DB column value), text (display). */
export interface FilterOptionItem {
  key: string;
  value: string;
  text: string;
}

export interface CheckboxFilterProps {
  label: string;
  options: FilterOptionItem[];
  /** Currently applied DB values. */
  value: string[];
  maxLabelLength?: number;
  /** Whether this filter has been modified from its default */
  isModified?: boolean;
  /** Called when the user clicks the reset icon */
  onReset?: () => void;
  /** Called with selected DB values on apply. */
  onApply: (values: string[]) => void;
}

/**
 * Checkbox list filter — for cardinality ≤ 200.
 *
 * Tracks **value** (DB column value) internally and returns value[] via onApply.
 * Uses **key** for React keys. Displays **text** as the human-readable label.
 */
export const CheckboxFilter: React.FC<CheckboxFilterProps> = ({
  label,
  options,
  value = [],
  maxLabelLength = 50,
  isModified = false,
  onReset,
  onApply,
}) => {
  const styles = useStyles();
  const [draft, setDraft] = React.useState<string[]>(value);

  React.useEffect(() => {
    setDraft(value);
  }, [value]);

  const allValues = React.useMemo(() => options.map((o) => o.value), [options]);

  /** Lookup map: value → text for display purposes. */
  const textByValue = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const o of options) map.set(o.value, o.text);
    return map;
  }, [options]);

  const isAllSelected =
    draft.length === allValues.length && allValues.length > 0;

  function toggle(val: string) {
    if (val === ALL_SENTINEL) {
      setDraft(isAllSelected ? [] : [...allValues]);
      return;
    }
    setDraft((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]
    );
  }

  function apply() {
    const toApply = draft.length === allValues.length ? [] : draft;
    onApply(toApply);
  }

  function cancel() {
    setDraft(value);
  }

  // ── Display value for chip ──────────────────────────────────────────────

  function buildDisplayValue(): string {
    if (!value.length || value.length === allValues.length) return "All";
    const displayText = value.map((v) => textByValue.get(v) ?? v).join(", ");
    const full = `${label}: ${displayText}`;
    if (full.length <= maxLabelLength) return displayText;
    return `${value.length} of ${options.length}`;
  }

  const isDirty =
    draft.length !== value.length || draft.some((v) => !value.includes(v));

  return (
    <FilterChipWrapper
      label={label}
      displayValue={buildDisplayValue()}
      isModified={isModified}
      onReset={onReset ?? (() => {})}
      isDirty={isDirty}
      onApply={apply}
      onCancel={cancel}
      surfaceWidth="260px"
    >
      <Checkbox
        label="All"
        checked={isAllSelected}
        onChange={() => toggle(ALL_SENTINEL)}
      />
      <Divider style={{ marginTop: "4px", marginBottom: "4px" }} />
      <div className={styles.list}>
        {options.map((o) => (
          <Checkbox
            key={o.key}
            label={o.text}
            checked={draft.includes(o.value)}
            onChange={() => toggle(o.value)}
          />
        ))}
      </div>
    </FilterChipWrapper>
  );
};